import {
  differenceInDays,
  endOfMonth,
  endOfWeek,
  endOfYear,
  intervalToDuration,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subWeeks,
  subYears,
} from "date-fns";
import moment from "moment";
import {
  AggregationType,
  AggregationTypeUnion,
  ChartInterval,
} from "types/charts";
import { OLDate } from "utilities/dateFormat";

export const currentDate = new Date();
const weekStartDate = startOfWeek(new Date(), { weekStartsOn: 1 });
const monthStartDate = startOfMonth(new Date());
const lastWeekStartDate = startOfWeek(subWeeks(new Date(), 1), {
  weekStartsOn: 1,
});
const lastWeekEndDate = endOfWeek(subWeeks(new Date(), 1), {
  weekStartsOn: 1,
});
const lastMonthStartDate = startOfMonth(subMonths(new Date(), 1));
const lastMonthEndDate = endOfMonth(subMonths(new Date(), 1));
const yearStartDate = startOfYear(new Date());
const lastYearStartDate = startOfYear(subYears(new Date(), 1));
const lastYearEndDate = endOfYear(subYears(new Date(), 1));

export const getStartDate = (startDate: Date, daysDifference: number) =>
  OLDate(subDays(startDate, daysDifference), { formatString: "yyyy-MM-dd" });

export const formatDate = (date: Date) =>
  OLDate(date, { formatString: "yyyy-MM-dd" });

export const chartIntervals: ChartInterval[] = [
  {
    intervalLabel: "last30Days",
    aggregationType: AggregationType.DAY,
    intervalStartDate: getStartDate(currentDate, 30),
    intervalEndDate: formatDate(currentDate),
  },
  {
    intervalLabel: "today",
    aggregationType: AggregationType.DAY,
    intervalStartDate: formatDate(currentDate),
    intervalEndDate: formatDate(currentDate),
  },
  {
    intervalLabel: "yesterday",
    aggregationType: AggregationType.DAY,
    intervalStartDate: getStartDate(currentDate, 1),
    intervalEndDate: getStartDate(currentDate, 1),
  },
  {
    intervalLabel: "thisWeek",
    aggregationType: AggregationType.DAY,
    intervalStartDate: formatDate(weekStartDate),
    intervalEndDate: formatDate(currentDate),
  },
  {
    intervalLabel: "lastWeek",
    aggregationType: AggregationType.DAY,
    intervalStartDate: formatDate(lastWeekStartDate),
    intervalEndDate: formatDate(lastWeekEndDate),
  },
  {
    intervalLabel: "thisMonth",
    aggregationType: AggregationType.DAY,
    intervalStartDate: formatDate(monthStartDate),
    intervalEndDate: formatDate(currentDate),
  },
  {
    intervalLabel: "lastMonth",
    aggregationType: AggregationType.DAY,
    intervalStartDate: formatDate(lastMonthStartDate),
    intervalEndDate: formatDate(lastMonthEndDate),
  },
  {
    intervalLabel: "thisYear",
    aggregationType: AggregationType.MONTH,
    intervalStartDate: formatDate(yearStartDate),
    intervalEndDate: formatDate(currentDate),
  },
  {
    intervalLabel: "lastYear",
    aggregationType: AggregationType.MONTH,
    intervalStartDate: formatDate(lastYearStartDate),
    intervalEndDate: formatDate(lastYearEndDate),
  },
  { intervalLabel: "allTime" },
  {
    intervalLabel: "customDateRange",
  },
];

export const globalChartIntervals = chartIntervals.filter(
  (interval) => interval.intervalLabel !== "allTime",
);

export const getFormatByAggregationType = (type: AggregationTypeUnion) =>
  type === "year" ? "yyyy" : type === "month" ? "MM-yyyy" : "dd-MM-yyyy";

export const checkDateRange = (startDate: string, endDate: string) => {
  const formatDateToMonthFirst = (date: string) =>
    moment(date.replace(/-/g, "/"), "DD/MM/YYYY").format("MM/DD/YYYY");

  const parseToDateObject = (date: string) =>
    moment(date, "MM/DD/YYYY").toDate();

  const formattedStartDate = formatDateToMonthFirst(startDate);
  const formattedEndDate = formatDateToMonthFirst(endDate);

  const duration = intervalToDuration({
    start: parseToDateObject(formattedStartDate),
    end: parseToDateObject(formattedEndDate),
  });

  return duration;
};

const formatOpts = { formatString: "yyyy-MM-dd" };

export const getComparativeDate = (
  activeIntervalSettings: ChartInterval,
  type: "start" | "end",
) => {
  const { intervalStartDate, intervalEndDate, intervalLabel } =
    activeIntervalSettings;

  const endDateObj = moment(intervalEndDate, "YYYY-MM-DD").toDate();
  const startDateObj = moment(intervalStartDate, "YYYY-MM-DD").toDate();
  const dateToCalculate = type === "start" ? startDateObj : endDateObj;

  switch (intervalLabel) {
    case "last30Days":
      return getStartDate(dateToCalculate, 30);
    case "yesterday":
      return getStartDate(dateToCalculate, 1);
    case "thisWeek":
    case "lastWeek":
      return getStartDate(dateToCalculate, 7);
    case "thisMonth":
      return OLDate(subMonths(dateToCalculate, 1), formatOpts);
    case "lastMonth":
      const daysInMonth = moment(dateToCalculate).daysInMonth();

      return OLDate(subDays(dateToCalculate, daysInMonth), formatOpts);
    case "thisYear":
      const thisYearDate =
        type === "start" ? lastYearStartDate : subYears(new Date(), 1);
      return OLDate(thisYearDate, formatOpts);
    case "lastYear":
      const lastYearDate =
        type === "start"
          ? subYears(lastYearStartDate, 1)
          : subYears(lastYearEndDate, 1);
      return OLDate(lastYearDate, formatOpts);
    case "customDateRange":
      // Add 1 because the last day of the comparative period is the day
      // before the start of the main (compared) period
      const diffInDays = differenceInDays(endDateObj, startDateObj) + 1;
      return getStartDate(dateToCalculate, diffInDays);
    default:
      return intervalEndDate || "";
  }
};

export const showComparativeData = (intervalLabel: string) =>
  intervalLabel !== "allTime" && intervalLabel !== "today";

export const formatDateToParse = (
  date: string,
  aggregationType: AggregationTypeUnion,
) =>
  aggregationType === AggregationType.WEEK
    ? moment(date?.replace("-", "W")).format("YYYY-MM-DD")
    : date;

export const getFormattedSelectedDate = (date?: string) =>
  OLDate(date, {
    formatString: "d MMM yyyy",
  });
